import { call, put, takeLatest, delay } from 'redux-saga/effects';
import { push } from 'connected-react-router'
import * as Types from '../../actions/types';
import * as User from '../../../api/user';
import { errorCatch } from '../helper.saga'
import { Messages, Routes } from '../../../config';
import { displayNotification } from '../../actions/notification';
import {
  tokenDetails,
  userDetails,
  userInfo,
  verifyEmailValidFlag,
  verifyEmailResendFlag,
  userInfoLoading,
  tablesSearches,
  pushNotificationsSubscribe,
  pushNotificationsUnsubscribe
} from '../../actions/user';
import { showGlobalNotification } from '../../actions/globalNotification';
import { NOTIFICATION_SUCCESS } from '../../actions/types';

function* loginSaga(action) {
  let response;

  try {
    response = yield call( User.login, action.payload );

    if (response.data.data.access_token) {
      const {
        access_token,
        expires_at,
        refresh_token,
        expires_in
      } = response.data.data;
      yield put( tokenDetails( access_token, expires_at, refresh_token, expires_in ) );
      yield put( push( Routes.ROUTE_DASHBOARD ) );
      yield put( userDetails() );
    }
  } catch (e) {
    yield errorCatch( e, {
      400: Messages.EMAIL_DOES_NOT_EXIST,
      403: Messages.EMAIL_IS_NOT_ACTIVATED,
    } );
  }
}

function* userDetailsSaga(action) {
  let response;

  try {
    yield put( userInfoLoading() );

    response = yield call(User.getUserDetails);

    if (response.data.data.id) {
      const { id, first_name, last_name, email, groups, permissions, passkey_auth } = response.data.data;
      yield put( pushNotificationsSubscribe());
      yield put( userInfo( id, first_name, last_name, email, groups, permissions, passkey_auth ) );

      if (! action.payload.stopRedirect) {
        // Redirect user
        yield put( push( Routes.ROUTE_DASHBOARD ) );
      }
    }
  } catch (e) {
    // Clean user Info in State
    yield put( tokenDetails() );
    yield put( userInfo() );

    // Redirect user
    yield put( push( Routes.ROUTE_LOGIN ) );
    yield errorCatch( e );
  }
}

/**
 * Logout handler (call )
 * @returns {IterableIterator<*>}
 */
function* logoutUserSaga(action) {
  //Show spinner until request is done.
  try {
    yield call( User.logoutUser );

    // Clean user Info in State
    yield put( tokenDetails() );
    yield put( userInfo() );
    yield put( pushNotificationsUnsubscribe() );

    // Clean table filters
    yield put( tablesSearches() );

    // Redirect user
    yield put( push( Routes.ROUTE_LOGIN ) );
    // Notify user
    if (! action.payload.isUnauthorized) {
      yield put( showGlobalNotification( NOTIFICATION_SUCCESS, Messages.USER_LOGOUT_SUCCESSFUL ) );
    } else {
      yield put( displayNotification( Types.NOTIFICATION_ERROR, Messages.UNAUTHENTICATED_ERROR ) );
    }
  } catch (e) {
    yield errorCatch( e );
  }
}

function* forgotSaga(action) {
  let response;

  try {
    response = yield call( User.forgot, action.payload );

    // Always show generic message on 201 success for security (prevent user enumeration)
    if (response.status === 201) {
      yield put( showGlobalNotification( NOTIFICATION_SUCCESS, Messages.PASSWORD_RECOVER_SUCCESS ) );
    }
  } catch (e) {
    yield errorCatch( e, {
      400: 'concat',
    } );
  }
}

function* newPasswordSaga(action) {
  let response;

  try {
    response = yield call( User.reset, action.payload );

    if (response.data.data.id) {
      yield put( push( Routes.ROUTE_LOGIN ) );
      yield put( showGlobalNotification( NOTIFICATION_SUCCESS, Messages.PASSWORD_CHANGED_SUCCESS ) );
    }
  } catch (e) {
    yield errorCatch( e, {
      400: 'concat',
      404: Messages.TOKEN_EXPIRED_NOT_FOUND
    } );
  }
}

function* accountValidationSaga(action) {
  let response;

  try {
    response = yield call( User.accountValidation, action.payload );

    if (response.data.data.id) {
      const { id, first_name, last_name, email } = response.data.data;
      yield put( userInfo( id, first_name, last_name, email ) );
      yield put( verifyEmailValidFlag( true ) );
      yield put( verifyEmailResendFlag( false ) );
      // Redirect user
      yield put( push( Routes.ROUTE_LOGIN ) );

      yield delay();
      yield put( showGlobalNotification( NOTIFICATION_SUCCESS, Messages.VERIFICATION_EMAIL_SUCCESS ) );
    }
  } catch (e) {
    yield errorCatch( e, {
      400: 'concat'
    } );

    yield put( verifyEmailValidFlag( false ) );
    yield put( verifyEmailResendFlag( false ) );
  }
}

function* resendEmailSaga(action) {
  let response;

  try {
    response = yield call( User.resendEmail, action.payload );

    if (response.status === 204) {
      yield put( verifyEmailValidFlag( true ) );
      yield put( verifyEmailResendFlag( true ) );
      yield put( push( Routes.ROUTE_LOGIN ) );
      yield put( showGlobalNotification( NOTIFICATION_SUCCESS, Messages.VERIFICATION_EMAIL_SENT ) );
    }

  } catch (e) {
    yield errorCatch( e, {
      400: 'concat',
      404: Messages.EMAIL_DOES_NOT_EXIST,
    } );

    yield put( verifyEmailValidFlag( false ) );
    yield put( verifyEmailResendFlag( false ) );
  }
}

function* loginPasskeySaga() {
  let response;

  try {
    let responsePasskey = yield call( User.loginPasskey );

    if (! responsePasskey) {
      return;
    }

    response = yield call( User.login, { passkey_token: responsePasskey.token } );

    if (response.data.data.access_token) {
      const {
        access_token,
        expires_at,
        refresh_token,
        expires_in
      } = response.data.data;
      yield put( tokenDetails( access_token, expires_at, refresh_token, expires_in ) );
      yield put( push( Routes.ROUTE_DASHBOARD ) );
      yield put( userDetails() );
    }
  } catch (e) {
    yield errorCatch( e, {
      400: 'concat',
      403: Messages.EMAIL_IS_NOT_ACTIVATED,
    } );
  }
}

export default function* userAuthSaga() {
  yield takeLatest( Types.REQUEST_LOGIN, loginSaga );
  yield takeLatest( Types.USER_DETAILS, userDetailsSaga );
  yield takeLatest( Types.USER_LOGOUT, logoutUserSaga );
  yield takeLatest( Types.REQUEST_FORGOT, forgotSaga );
  yield takeLatest( Types.REQUEST_NEW_PASSWORD_FORGOT, newPasswordSaga );
  yield takeLatest( Types.REQUEST_VERIFICATION_USER, accountValidationSaga );
  yield takeLatest( Types.REQUEST_NEW_VERIFICATION_USER, resendEmailSaga );
  yield takeLatest( Types.REQUEST_LOGIN_PASSKEY, loginPasskeySaga );
}
